import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Plus, Pencil, Package, PackageMinus, PackagePlus } from "lucide-react";
import { STOCK_MOVEMENT_TYPES, money } from "@/lib/constants";

const EMPTY_PART = {
  sku: "", name: "", description: "", category: "", unit: "pza",
  unit_price: 0, unit_cost: 0, iva_rate: 0.16, track_stock: true,
  min_stock: 0, status: "active",
};

export default function Inventario() {
  const { isSuperAdmin, activeTenantId, branches, activeBranch } = useTenant();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PART);
  const [saving, setSaving] = useState(false);
  const [movDialog, setMovDialog] = useState(null); // {part, delta}
  const [movForm, setMovForm] = useState({ quantity_delta: 0, movement_type: "purchase", branch_id: "", unit_cost: "", notes: "" });

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};
  const canQuery = !isSuperAdmin || !!activeTenantId;

  async function fetchAll() {
    if (!canQuery) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/parts", { params: { ...tenantParam, search: search || undefined, include_inactive: true } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchAll, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeTenantId, isSuperAdmin]);

  function openCreate() { setEditing(null); setForm(EMPTY_PART); setSheetOpen(true); }
  function openEdit(p) { setEditing(p); setForm({ ...EMPTY_PART, ...p }); setSheetOpen(true); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        unit_price: Number(form.unit_price) || 0,
        unit_cost: form.unit_cost === "" || form.unit_cost === null ? null : Number(form.unit_cost),
        iva_rate: Number(form.iva_rate) || 0,
        min_stock: Number(form.min_stock) || 0,
      };
      if (editing) {
        await api.patch(`/parts/${editing.id}`, payload);
        toast.success("Refacción actualizada");
      } else {
        await api.post("/parts", payload, { params: tenantParam });
        toast.success("Refacción creada");
      }
      setSheetOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  function openMovement(part, direction) {
    setMovForm({
      quantity_delta: direction === "out" ? -1 : 1,
      movement_type: direction === "out" ? "adjustment" : "purchase",
      branch_id: activeBranch?.id || branches[0]?.id || "",
      unit_cost: part.unit_cost || "",
      notes: "",
    });
    setMovDialog({ part, direction });
  }

  async function saveMovement() {
    if (!movDialog) return;
    try {
      await api.post("/stock-movements", {
        part_id: movDialog.part.id,
        branch_id: movForm.branch_id,
        quantity_delta: Number(movForm.quantity_delta),
        movement_type: movForm.movement_type,
        unit_cost: movForm.unit_cost === "" ? null : Number(movForm.unit_cost),
        notes: movForm.notes || null,
      }, { params: tenantParam });
      toast.success("Movimiento registrado");
      setMovDialog(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="space-y-6" data-testid="page-inventario">
      <PageHeader
        title="Inventario"
        description="Refacciones y servicios que se cotizan a los clientes. El stock se descuenta al cerrar la venta."
        actions={<Button className="btn-tenant" onClick={openCreate} disabled={!canQuery} data-testid="btn-new-part"><Plus className="h-4 w-4 mr-2" /> Nueva refacción</Button>}
      />

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar por SKU, nombre o categoría…" className="pl-9 h-10" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-parts" />
        </div>
        <div className="text-xs text-muted-foreground ml-auto" data-testid="parts-count">{items.length} refacc{items.length === 1 ? "ión" : "iones"}</div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10" />)}</div> :
         items.length === 0 ? (
           <EmptyState title={canQuery ? "Sin refacciones" : "Selecciona un tenant"} description="Crea la primera refacción o servicio para poder cotizarlo." icon={Package} action={canQuery && <Button className="btn-tenant" onClick={openCreate}><Plus className="h-4 w-4 mr-2" /> Crear refacción</Button>} />
         ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead className="text-right">Precio</TableHead>
                <TableHead className="text-right">Stock total</TableHead>
                <TableHead>Estatus</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} className="tbl-row" data-testid={`row-part-${p.id}`}>
                  <TableCell className="mono text-xs font-medium">{p.sku}</TableCell>
                  <TableCell>
                    <div className="font-medium">{p.name}</div>
                    {!p.track_stock && <Badge variant="secondary" className="text-[10px]">Servicio</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.category || "—"}</TableCell>
                  <TableCell className="text-right mono text-sm">{money(p.unit_price)}</TableCell>
                  <TableCell className="text-right">
                    {p.track_stock ? (
                      <span className={`text-sm mono ${p.total_stock <= p.min_stock ? "text-red-600 font-medium" : ""}`}>
                        {p.total_stock} {p.unit}
                      </span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell><Badge className={p.status === "active" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>{p.status === "active" ? "Activa" : "Inactiva"}</Badge></TableCell>
                  <TableCell className="text-right">
                    {p.track_stock && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openMovement(p, "in")} data-testid={`btn-stock-in-${p.id}`}><PackagePlus className="h-4 w-4 text-emerald-600" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => openMovement(p, "out")} data-testid={`btn-stock-out-${p.id}`}><PackageMinus className="h-4 w-4 text-red-600" /></Button>
                      </>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`btn-edit-part-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="sheet-part">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar refacción" : "Nueva refacción"}</SheetTitle>
            <SheetDescription>Servicios (mano de obra) deben desactivar "control de stock".</SheetDescription>
          </SheetHeader>
          <form onSubmit={save} className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>SKU *</Label><Input required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} className="mono" data-testid="part-sku" /></div>
              <div className="space-y-2"><Label>Unidad</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="part-unit" /></div>
            </div>
            <div className="space-y-2"><Label>Nombre *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="part-name" /></div>
            <div className="space-y-2"><Label>Categoría</Label><Input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="part-category" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Precio venta</Label><Input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="part-price" /></div>
              <div className="space-y-2"><Label>Costo</Label><Input type="number" step="0.01" value={form.unit_cost ?? ""} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} data-testid="part-cost" /></div>
              <div className="space-y-2"><Label>IVA</Label><Input type="number" step="0.01" value={form.iva_rate} onChange={(e) => setForm({ ...form, iva_rate: e.target.value })} data-testid="part-iva" /></div>
              <div className="space-y-2"><Label>Stock mínimo</Label><Input type="number" step="0.01" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} data-testid="part-min-stock" /></div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-md border border-border">
              <div><Label>Control de stock</Label><p className="text-xs text-muted-foreground">Desactiva para servicios (mano de obra).</p></div>
              <Switch checked={form.track_stock} onCheckedChange={(v) => setForm({ ...form, track_stock: v })} data-testid="part-track-stock" />
            </div>
            <div className="space-y-2"><Label>Descripción</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="part-desc" /></div>
            <SheetFooter>
              <Button type="button" variant="outline" onClick={() => setSheetOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="btn-tenant" data-testid="part-save">{saving ? "Guardando…" : editing ? "Guardar" : "Crear refacción"}</Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <Sheet open={!!movDialog} onOpenChange={(o) => !o && setMovDialog(null)}>
        <SheetContent className="w-full sm:max-w-md" data-testid="sheet-movement">
          <SheetHeader>
            <SheetTitle>Movimiento de inventario</SheetTitle>
            <SheetDescription>{movDialog?.part?.name} · <span className="mono text-xs">{movDialog?.part?.sku}</span></SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={movForm.movement_type} onValueChange={(v) => setMovForm({ ...movForm, movement_type: v })}>
                <SelectTrigger data-testid="mov-type"><SelectValue /></SelectTrigger>
                <SelectContent>{STOCK_MOVEMENT_TYPES.filter(t => !["sale"].includes(t.value)).map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sucursal</Label>
              <Select value={movForm.branch_id} onValueChange={(v) => setMovForm({ ...movForm, branch_id: v })}>
                <SelectTrigger data-testid="mov-branch"><SelectValue /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Cantidad (positivo=entrada, negativo=salida)</Label><Input type="number" step="0.01" value={movForm.quantity_delta} onChange={(e) => setMovForm({ ...movForm, quantity_delta: e.target.value })} data-testid="mov-qty" /></div>
            <div className="space-y-2"><Label>Costo unitario (opcional)</Label><Input type="number" step="0.01" value={movForm.unit_cost} onChange={(e) => setMovForm({ ...movForm, unit_cost: e.target.value })} data-testid="mov-cost" /></div>
            <div className="space-y-2"><Label>Notas</Label><Textarea rows={2} value={movForm.notes} onChange={(e) => setMovForm({ ...movForm, notes: e.target.value })} data-testid="mov-notes" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setMovDialog(null)}>Cancelar</Button>
              <Button onClick={saveMovement} className="btn-tenant" data-testid="mov-save">Registrar</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
